/**
 * CR-6518: the note a consolidated delivery carries while only SOME of the
 * weeks it covers have been forgiven.
 *
 * A supplier may bundle several scheduled weeks into one delivery, and an OTIF
 * override is a per-week decision. The delivery only reads "Overridden (…)"
 * once every week it covers is settled (see
 * `purchasing/services/delivery_status_rollup.py` — the backend owns that
 * rule). Until then the grid correctly still says `Late`, because the delivery
 * IS still late for its other weeks.
 *
 * Correct, but silent: a buyer who forgave one week of four saw an unchanged
 * `Late` and no sign their decision had registered anywhere, so it read as a
 * broken button. This says what actually happened.
 */
import { isOverridden, countsAsOnTime } from '../../../../lib/onTimeStatus';

/** The per-week allocation shape this needs — a subset of GRNScheduleAllocation. */
export interface WeekAllocation {
  on_time_status?: string | null;
}

/**
 * A short note naming how many of a delivery's weeks have been overridden,
 * or null when there is nothing worth saying.
 *
 * Returns null when the delivery covers fewer than two weeks (nothing was
 * consolidated, so the status already tells the whole story), when no week has
 * been overridden, and when every week is settled — in that last case the
 * delivery itself reads "Overridden (…)" and a count would only repeat it.
 *
 * @param allocations - the delivery's per-week allocations
 * @returns e.g. "1 of 4 weeks overridden", or null
 */
export function partialOverrideNote(
  allocations: readonly WeekAllocation[] | null | undefined,
): string | null {
  if (!allocations || allocations.length < 2) return null;

  const total = allocations.length;
  const overridden = allocations.filter((a) => isOverridden(a.on_time_status)).length;
  if (overridden === 0) return null;

  // Every week settled: the delivery's own status already says so.
  const settled = allocations.filter((a) => countsAsOnTime(a.on_time_status)).length;
  if (settled === total) return null;

  const weeks = total === 1 ? 'week' : 'weeks';
  return `${overridden} of ${total} ${weeks} overridden`;
}
